import React, { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import './GlobalSearch.css';

const CommandPalette = lazy(() => import('./CommandPalette'));

export const SearchContext = createContext({ openSearch: () => {} });

export const useSearch = () => useContext(SearchContext);

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');

/** "⌘K" on a Mac, "Ctrl+K" elsewhere. */
export const shortcutLabel = () => (isMac() ? '⌘K' : 'Ctrl+K');

/** Cmd+K on a Mac, Ctrl+K elsewhere (either works on both, as in other apps); never with Alt or Shift. */
export const isSearchShortcut = (event) => (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey
  && String(event.key || '').toLowerCase() === 'k';

/** Code editors keep their own Cmd/Ctrl+K chords (Monaco: Cmd+K Cmd+C comments, Cmd+K Cmd+F formats, …). */
export const CODE_EDITOR_SELECTOR = '.monaco-editor, .monaco-diff-editor, .cm-editor, .ace_editor, [data-code-editor]';

/** Whether the key event comes from inside a code editor (its target, or the focused element as a fallback). */
export const isInCodeEditor = (event) => {
  const candidates = [event?.target, typeof document !== 'undefined' ? document.activeElement : null];
  return candidates.some((node) => node && typeof node.closest === 'function' && Boolean(node.closest(CODE_EDITOR_SELECTOR)));
};

/**
 * Flow's global search: Cmd/Ctrl+K anywhere (also in the chat box: the listener runs first, in the capture phase), the
 * Search box at the top of the sidebar, or the magnifier in the phone's top bar. Not inside a code editor: there
 * Cmd/Ctrl+K belongs to the editor's own chords, and the palette stays one click away in the sidebar.
 */
export function SearchProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [initial, setInitial] = useState('');
  const openSearch = useCallback((text = '') => { setInitial(typeof text === 'string' ? text : ''); setOpen(true); }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (!isSearchShortcut(event) || isInCodeEditor(event)) { return; }
      event.preventDefault();
      event.stopPropagation();
      setInitial('');
      setOpen((current) => !current);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const value = useMemo(() => ({ openSearch }), [openSearch]);
  return (
    <SearchContext.Provider value={value}>
      {children}
      {open && (
        <Suspense fallback={null}>
          <CommandPalette initialQuery={initial} onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </SearchContext.Provider>
  );
}

SearchProvider.propTypes = { children: PropTypes.node };

/**
 * The search box: looks like an input, opens the palette. `compact`: an icon only (collapsed sidebar). `label`: its
 * text ("Search"); `onBeforeOpen`: called first (the phone's Menu sheet closes itself).
 */
export function SearchTrigger({ compact = false, className = '', label = 'Search', onBeforeOpen = null }) {
  const { openSearch } = useSearch();
  return (
    <button
      aria-keyshortcuts="Meta+K Control+K"
      aria-label="Search everything"
      className={`flow-search-trigger${compact ? ' compact' : ''}${className ? ` ${className}` : ''}`}
      onClick={() => { onBeforeOpen?.(); openSearch(); }}
      title={`Search (${shortcutLabel()})`}
      type="button"
    >
      <MagnifyingGlassIcon size={16} />
      {!compact && <span className="flow-search-trigger-text">{label}</span>}
      {!compact && <kbd className="flow-search-kbd flow-search-trigger-kbd">{shortcutLabel()}</kbd>}
    </button>
  );
}

SearchTrigger.propTypes = {
  className: PropTypes.string, compact: PropTypes.bool, label: PropTypes.string, onBeforeOpen: PropTypes.func,
};

/**
 * The quiet "Search everything ⌘K" at the foot of a menu (the project's group menus): opens the palette, where every
 * tab and action of the project is. `full`: with its words; otherwise the magnifier and the shortcut only.
 * `onBeforeOpen`: called first (the menu closes itself).
 */
export function SearchHint({ full = false, onBeforeOpen = null }) {
  const { openSearch } = useSearch();
  const keys = shortcutLabel();
  return (
    <button
      aria-keyshortcuts="Meta+K Control+K"
      aria-label={`Search everything (${keys})`}
      className="flow-search-hint"
      data-testid="search-hint"
      onClick={() => { onBeforeOpen?.(); openSearch(); }}
      title={`Search everything: ${keys}`}
      type="button"
    >
      <MagnifyingGlassIcon aria-hidden size={13} />
      {full && <span>Search everything</span>}
      <kbd className="flow-search-kbd">{keys}</kbd>
    </button>
  );
}

SearchHint.propTypes = { full: PropTypes.bool, onBeforeOpen: PropTypes.func };
